import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, Bell, ShoppingCart, Star, Package, BarChart3, Shield, Send } from 'lucide-react';
import { toast } from 'sonner';

const PREF_CONFIG = [
  { key: 'welcome_email', icon: Mail, color: 'text-emerald-500', bg: 'bg-emerald-500/10', type: 'transactional' },
  { key: 'order_confirmation', icon: ShoppingCart, color: 'text-blue-500', bg: 'bg-blue-500/10', type: 'transactional' },
  { key: 'order_status_updates', icon: Bell, color: 'text-purple-500', bg: 'bg-purple-500/10', type: 'transactional' },
  { key: 'new_review_alert', icon: Star, color: 'text-amber-500', bg: 'bg-amber-500/10', type: 'notification' },
  { key: 'low_stock_alert', icon: Package, color: 'text-red-500', bg: 'bg-red-500/10', type: 'notification' },
  { key: 'weekly_report', icon: BarChart3, color: 'text-orange-500', bg: 'bg-orange-500/10', type: 'marketing' },
];

export default function EmailPrefsPage() {
  const { API, authHeaders, activeBranch } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const l = (ar, en) => isAr ? ar : en;

  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const labels = {
    welcome_email: { ar: 'رسالة الترحيب عند التسجيل', en: 'Welcome email on registration' },
    order_confirmation: { ar: 'تأكيد الطلب للعميل', en: 'Order confirmation to customer' },
    order_status_updates: { ar: 'تحديثات حالة الطلب', en: 'Order status updates' },
    new_review_alert: { ar: 'إشعار عند تقييم جديد', en: 'Alert on new review' },
    low_stock_alert: { ar: 'تنبيه المخزون المنخفض', en: 'Low stock alert' },
    weekly_report: { ar: 'التقرير الأسبوعي للأداء', en: 'Weekly performance report' },
  };

  const descriptions = {
    welcome_email: { ar: 'إيميل ترحيبي عند إنشاء الحساب', en: 'Welcome email when account is created' },
    order_confirmation: { ar: 'إيميل تأكيد تفاصيل الطلب للعميل', en: 'Order details confirmation to customer' },
    order_status_updates: { ar: 'إشعار بتغيير حالة الطلب (تحضير، جاهز، توصيل)', en: 'Notify on status change (preparing, ready, delivered)' },
    new_review_alert: { ar: 'إيميل عندما يترك عميل تقييم جديد', en: 'Email when a customer leaves a review' },
    low_stock_alert: { ar: 'تنبيه عندما ينخفض مخزون صنف عن 5 وحدات', en: 'Alert when item stock drops below 5' },
    weekly_report: { ar: 'ملخص أسبوعي بالإيرادات والطلبات والتوصيات الذكية', en: 'Weekly summary with revenue, orders, and AI tips' },
  };

  const typeLabels = {
    transactional: { ar: 'إيميلات المعاملات', en: 'Transactional Emails' },
    notification: { ar: 'إشعارات وتنبيهات', en: 'Notifications & Alerts' },
    marketing: { ar: 'تقارير وتسويق', en: 'Reports & Marketing' },
  };

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/email-preferences`, authHeaders());
      setPrefs(data);
    } catch { /* silent */ }
    setLoading(false);
  }, [API, authHeaders]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const togglePref = async (key) => {
    const newVal = !prefs[key];
    setPrefs(p => ({ ...p, [key]: newVal }));
    setSaving(true);
    try {
      await axios.put(`${API}/email-preferences`, { [key]: newVal }, authHeaders());
      toast.success(l('تم الحفظ', 'Saved'));
    } catch {
      setPrefs(p => ({ ...p, [key]: !newVal }));
      toast.error(l('حدث خطأ', 'Error'));
    }
    setSaving(false);
  };

  const sendTestReport = async () => {
    if (!activeBranch?.id) return;
    setSendingReport(true);
    try {
      const { data } = await axios.post(
        `${API}/email-preferences/send-weekly-report?branch_id=${activeBranch.id}`, {}, authHeaders()
      );
      if (data.sent) toast.success(l('تم إرسال التقرير لبريدك', 'Report sent to your email'));
      else toast.error(l('فشل الإرسال', 'Send failed'));
    } catch {
      toast.error(l('حدث خطأ', 'Error'));
    }
    setSendingReport(false);
  };

  const grouped = {};
  PREF_CONFIG.forEach(p => {
    if (!grouped[p.type]) grouped[p.type] = [];
    grouped[p.type].push(p);
  });

  return (
    <ModuleLayout>
      <div className="space-y-6 max-w-2xl" data-testid="email-prefs-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }}>
            <Mail className="w-6 h-6 inline-block me-2 text-amber-500" />
            {l('تفضيلات البريد الإلكتروني', 'Email Preferences')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {l('تحكم في الإيميلات التي تصلك من المنصة', 'Control which emails you receive from the platform')}
          </p>
        </div>

        {/* PDPL Notice */}
        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">{l('حماية بياناتك', 'Data Protection')}</p>
              <p className="text-xs text-amber-700/70 dark:text-amber-400/70 mt-0.5 leading-relaxed">
                {l(
                  'نلتزم بنظام حماية البيانات الشخصية السعودي (PDPL). يمكنك إيقاف أي نوع من الإيميلات في أي وقت. الإيميلات المعاملاتية ضرورية لتقديم الخدمة.',
                  'We comply with Saudi PDPL. You can disable any email type at any time. Transactional emails are necessary for service delivery.'
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <h3 className="text-sm font-bold text-muted-foreground mb-3 uppercase tracking-wider">
                {typeLabels[type]?.[isAr ? 'ar' : 'en'] || type}
              </h3>
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  {items.map(({ key, icon: Icon, color, bg }) => (
                    <div key={key} className="flex items-center gap-4 px-4 py-3.5" data-testid={`pref-${key}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bg} flex-shrink-0`}>
                        <Icon className={`w-4.5 h-4.5 ${color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{labels[key]?.[isAr ? 'ar' : 'en']}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{descriptions[key]?.[isAr ? 'ar' : 'en']}</p>
                      </div>
                      <button
                        onClick={() => togglePref(key)}
                        disabled={saving}
                        className={`relative w-11 h-6 rounded-full transition-colors ${prefs[key] ? 'bg-amber-500' : 'bg-muted'}`}
                        data-testid={`toggle-${key}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${prefs[key] ? (isAr ? 'start-0.5' : 'start-[22px]') : (isAr ? 'start-[22px]' : 'start-0.5')}`} />
                      </button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          ))
        )}

        {/* Test Weekly Report */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground">{l('إرسال تقرير تجريبي', 'Send Test Report')}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{l('أرسل نسخة من التقرير الأسبوعي لبريدك الآن', 'Send a copy of the weekly report to your email now')}</p>
              </div>
              <Button onClick={sendTestReport} disabled={sendingReport} variant="outline" className="gap-2 text-xs" data-testid="send-test-report-btn">
                <Send className="w-3.5 h-3.5" />
                {sendingReport ? '...' : l('إرسال الآن', 'Send Now')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </ModuleLayout>
  );
}
